import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { biometricLogin, hasDevicePasskey } from "../lib/webauthn.js";
import { useAuth } from "../context/AuthContext.jsx";

// Two-user login (Mike + Lara). Pick who you are, then authenticate with Face ID
// (when enrolled) or a PIN. First-time users set a PIN with the shared access code.
export default function Login() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [demo, setDemo] = useState(false);

  async function enterDemo() {
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/demo", { method: "POST" });
      await refresh();
      navigate("/", { replace: true });
    } catch {
      setError("Demo isn't available right now.");
      setBusy(false);
    }
  }

  async function tryFaceId(user) {
    setBusy(true);
    setError("");
    try {
      await biometricLogin(user.id);
      await refresh();
      navigate("/", { replace: true });
      return true;
    } catch (err) {
      // NotAllowedError = user cancelled the Face ID sheet; 404 = no passkey on
      // this domain yet. Both fall through to the PIN form quietly.
      if (err?.name !== "NotAllowedError" && err?.status !== 404) {
        setError(err?.status === 429 ? "Too many attempts — try again later." : "Face ID didn't work — use your PIN.");
      }
      return false;
    } finally {
      setBusy(false);
    }
  }

  const loadUsers = () =>
    api("/api/auth/users")
      .then((d) => {
        setUsers(d?.users ?? []);
        setDemo(!!d?.demo);
      })
      .catch(() =>
        setError("Backend not reachable. Run `npm run build && npm run pages:dev` for full login."),
      );

  useEffect(() => {
    loadUsers();
  }, []);

  const needsSetup = selected && !selected.has_pin;

  function back() {
    setSelected(null);
    setPin("");
    setAccessCode("");
    setError("");
  }

  async function submit(e) {
    e.preventDefault();
    if (!selected || pin.length < 4) return;
    setBusy(true);
    setError("");
    try {
      if (needsSetup) {
        await api("/api/auth/setup", { method: "POST", body: { userId: selected.id, accessCode, pin } });
      }
      await api("/api/auth/pin", { method: "POST", body: { userId: selected.id, pin } });
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      if (err.status === 429) setError("Too many attempts — try again later.");
      else if (err.status === 403) setError("Invalid access code.");
      else setError(needsSetup ? "Setup failed — check the access code." : "Incorrect PIN.");
      setPin("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="brand-gradient flex min-h-full flex-col items-center justify-center px-6 py-12 text-white">
      <div className="mb-8 flex flex-col items-center gap-3">
        <img src="/icons/icon.svg" alt="" className="h-16 w-16 rounded-2xl shadow-lg" />
        <h1 className="text-2xl font-semibold">
          Business Tracker
          {/* Build-time flavour (vite.config.js): the staging build says so in words, not just in its icon. */}
          {__BT_FLAVOR__ === "staging" && (
            <span className="ml-2 rounded-md bg-red-600 px-2 py-0.5 align-middle text-xs font-bold tracking-widest text-white">DEV</span>
          )}
        </h1>
        <p className="text-sm text-white/70">AI Engines bookkeeping</p>
      </div>

      <div className="w-full max-w-sm rounded-2xl bg-white/95 p-6 text-slate-900 shadow-xl">
        {!selected ? (
          <div className="space-y-3">
            {demo && (
              <>
                <button
                  onClick={enterDemo}
                  disabled={busy}
                  className="w-full rounded-xl bg-navy-700 py-3.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {busy ? "Opening…" : "▶️ Try the demo"}
                </button>
                <p className="text-center text-xs text-slate-400">
                  Sample data only — nothing here is connected to a real bank, inbox, or payment account.
                </p>
              </>
            )}
            <p className="text-center text-sm font-medium text-slate-500">Who's signing in?</p>
            {users.length === 0 && !error && <p className="text-center text-sm text-slate-400">Loading…</p>}
            {users.map((u) => (
              <button
                key={u.id}
                disabled={busy}
                onClick={() => {
                  setSelected(u);
                  setError("");
                  // Auto-trigger Face ID only if THIS device holds a passkey —
                  // a credential enrolled on another device would send iOS into
                  // a dead-end "no passkey found" sheet.
                  if (u.has_biometric && hasDevicePasskey(u.id)) tryFaceId(u);
                }}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-left font-medium hover:border-navy-600 hover:bg-slate-50 disabled:opacity-50"
              >
                <span>{u.display_name}</span>
                <span className="text-xs text-slate-400">
                  {u.has_biometric && hasDevicePasskey(u.id) ? "Face ID" : u.has_pin ? "PIN" : "Set up"}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <button type="button" onClick={back} className="text-sm text-navy-600">
              ← Not {selected.display_name}?
            </button>

            {/* Manual button stays available whenever the account has ANY passkey:
                devices enrolled before the per-device marker existed recover by
                tapping it once (success sets the marker; cancel falls to PIN). */}
            {selected.has_biometric && (
              <button
                type="button"
                disabled={busy}
                onClick={() => tryFaceId(selected)}
                className="w-full rounded-xl bg-navy-700 py-3 font-semibold text-white disabled:opacity-50"
              >
                {busy ? "Waiting for Face ID…" : "Sign in with Face ID"}
              </button>
            )}

            {needsSetup && (
              <>
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  First time for {selected.display_name}. Enter the shared access code and choose a PIN.
                </p>
                <label className="block text-sm font-medium text-slate-700">
                  Access code
                  <input
                    type="password"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-navy-600 focus:outline-none"
                  />
                </label>
              </>
            )}

            <label className="block text-sm font-medium text-slate-700">
              {needsSetup ? "Choose a PIN (4–6 digits)" : "Enter your PIN"}
              <input
                autoFocus={!needsSetup}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-2xl tracking-[0.5em] focus:border-navy-600 focus:outline-none"
              />
            </label>

            <button
              type="submit"
              disabled={busy || pin.length < 4 || (needsSetup && !accessCode)}
              className="w-full rounded-xl bg-navy-700 py-3 font-semibold text-white disabled:opacity-50"
            >
              {busy ? "Working…" : needsSetup ? "Set PIN & sign in" : "Sign in"}
            </button>
          </form>
        )}

        {error && <p className="mt-4 text-center text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
}
